import React from 'react';
import { AlertCircle, RefreshCw } from 'lucide-react';

export class ResumeErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Safe logging — never expose sensitive data
    console.error('%c[ResumeErrorBoundary] ERROR CAUGHT', 'color: red; font-size: 16px; font-weight: bold');
    console.error('[ResumeErrorBoundary] Error:', {
      message: error?.message,
      name: error?.name,
      stack: error?.stack,
      componentStack: info?.componentStack
    });
    
    // Log current state
    console.error('[ResumeErrorBoundary] Current DOM state:', {
      url: window.location.href,
      localStorage: Object.keys(localStorage),
      builderVersion: document.querySelector('[data-builder-version]')?.getAttribute('data-builder-version')
    });
    
    // Try to identify the failing component
    const componentMatch = info?.componentStack?.match(/at (\w+)/);
    if (componentMatch) {
      console.error('[ResumeErrorBoundary] Failing component:', componentMatch[1]);
    }
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const { onClose, minimal } = this.props;

    if (minimal) {
      return (
        <div className="flex flex-col items-center justify-center p-8 text-center">
          <AlertCircle className="w-8 h-8 text-red-500 mb-3" />
          <p className="text-gray-600 text-sm mb-3">This section failed to load.</p>
          <button
            onClick={this.handleReset}
            className="text-blue-600 text-sm font-medium hover:underline flex items-center gap-1"
          >
            <RefreshCw className="w-3 h-3" /> Try again
          </button>
        </div>
      );
    }

    return (
      <div className="fixed inset-0 bg-white z-[8001] flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl border border-gray-100 max-w-md w-full p-8 text-center">
          <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-5">
            <AlertCircle className="w-8 h-8 text-red-500" />
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">Resume Builder Error</h3>
          <p className="text-gray-500 text-sm mb-6 leading-relaxed">
            Something went wrong. Your form data is saved automatically — you won't lose any progress.
          </p>
          <div className="bg-red-50 text-red-700 text-left p-4 rounded-xl mt-4 max-h-[300px] overflow-y-auto text-xs whitespace-pre-wrap font-mono">
            <p className="font-bold mb-1">Error Details:</p>
            {this.state.error && this.state.error.message}
            <br />
            {this.state.error && this.state.error.stack}
          </div>
          
          <div className="flex items-center gap-3 w-full mt-4">
            <button
              onClick={this.handleReset}
              className="flex-1 flex items-center justify-center gap-2 bg-blue-600 text-white py-2.5 px-4 rounded-xl font-semibold hover:bg-blue-700 transition-colors"
            >
              <RefreshCw className="w-4 h-4" /> Try Again
            </button>
            {onClose && (
              <button
                onClick={onClose}
                className="flex-1 bg-gray-100 text-gray-700 py-2.5 px-4 rounded-xl font-semibold hover:bg-gray-200 transition-colors"
              >
                Close
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }
}

export default ResumeErrorBoundary;
